import { CellPosition, NEW_ROW_ID } from "@app/grid/GridCell";
import { UTCDate } from "@date-fns/utc";
import Field, { AggregationType, fieldToOutputType, FieldType, getDestinationField, isCountAggregation, TYPE_ITEMS_DATE_FORMAT, TYPE_ITEMS_FIELD_KEY, TYPE_ITEMS_PERIOD_TYPE } from "@dto/fields";
import { aggregationToPeriodType, getMonthPeriod, getQuarterPeriod, getWeekPeriod, Period, PeriodMonthJanuary, PeriodType, PeriodTypeDay, PeriodTypeMonth, PeriodTypeQuarter, PeriodTypeWeek, PeriodTypeYear } from "@dto/period";
import { extractFirstLinkPath, isRootPath, Item, Line, MetaLine } from "@dto/records";
import User from "@dto/Users";
import { ASC, DESC, SortType } from "@dto/views";
import { format, parse } from "date-fns";
import { t, TFunction } from "i18next";
import { useAppStore, useDataStore, useUserStore } from "src/store";
import { CURRENCY_SYMBOLS, DECIMAL_DEFAULT_DECIMAL_PLACES, DEFAULT_CURRENCY_SYMBOL, DEFAULT_CURRENCY_SYMBOL_BEFORE, DEFAULT_DATE_FORMAT, DEFAULT_NUMBER_FORMAT, DEFAULT_PERIOD_FORMAT, DEFAULT_TEXT_FORMAT } from "./constants";

export const NUMBER_FORMATS = new Map([
  ["NONE", "none"],
  ["FR", "fr-FR"],
  ["US", "en-US"],
  ["DE", "de-DE"]
]);

export const TEXT_FORMATS = new Map([
  ["NONE", "none"],
  ["UPPERCASE", "uppercase"],
  ["LOWERCASE", "lowercase"],
  ["CAPITALIZE", "capitalize"],
]);

export const DATE_FORMATS = new Map([
  ["NONE", "yyyy-MM-dd"],
  ["FR", "dd/MM/yyyy"],
  ["US", "MM/dd/yyyy"],
  ["DE", "dd.MM.yyyy"],
  ["ISO", "yyyy-MM-dd"],
  ["FI", "d.M.yyyy"],
  ["NL", "d-M-yyyy"],
  ["PT", "dd-MM-yyyy"],
  ["FULL", "d MMMM yyyy"],
  ["FULL_WITH_DAY", "EEEE d MMMM yyyy"],
  ["DAY_MONTH", "d MMMM"],
  ["DAY_MONTH_REDUCED", "dd/MM"],
  ["ONLY_DAY", "dd"],
]);

export const MONTH_FORMATS = new Map([
  ["NONE", "none"],
  ["MONTH_ONLY", "MMMM"],
  ["MONTH_ONLY_SHORT", "MMM"],
  ["MONTH_ONLY_NUMBER", "MM"],
  ["MONTH_AND_YEAR", "MMMM yyyy"],
  ["MONTH_AND_YEAR_SHORT", "MMM yyyy"],
  ["MONTH_AND_YEAR_NUMBER", "MM/yyyy"],
]);

export const QUARTER_FORMATS = new Map([
  ["NONE", "none"],
  ["QUARTER_ONLY", "QQQQ"],
  ["QUARTER_ONLY_SHORT", "QQQ"],
  ["QUARTER_ONLY_NUMBER", "QQ"],
  ["QUARTER_AND_YEAR", "QQQQ yyyy"],
  ["QUARTER_AND_YEAR_SHORT", "QQQ yyyy"],
]);

export const WEEK_FORMATS = new Map([
  ["NONE", "none"],
  ["WEEK_ONLY", "WWWW"],
  ["WEEK_ONLY_SHORT", "WWW"],
  ["WEEK_ONLY_NUMBER", "WW"],
  ["WEEK_AND_YEAR", "WWWW yyyy"],
  ["WEEK_AND_YEAR_SHORT", "WWW yyyy"],
]);

export function getPeriodFormats(periodType: PeriodType): Map<string, string> | undefined {
  switch (periodType) {
    case PeriodTypeMonth:
      return MONTH_FORMATS;
    case PeriodTypeQuarter:
      return QUARTER_FORMATS;
    case PeriodTypeWeek:
      return WEEK_FORMATS;
    case PeriodTypeDay:
      return DATE_FORMATS;
  }
  return undefined;
}

export const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;

/** Value sent by the backend in a formula cell whose formula could not be computed for its line */
export const FORMULA_ERROR_VALUE = "Error";

/**
 * Whether a cell value is a formula error: the backend marker in a field computed by a formula
 * (directly, or through links). A text "Error" typed in a field without formula is a regular value.
 */
export function isFormulaError(value: any, field: Field | null | undefined, fieldByKeys: Map<string, Field> | null = null): boolean {
  if (value !== FORMULA_ERROR_VALUE || !field) {
    return false;
  }
  return !!getDestinationField(field, fieldByKeys)?.formula;
}

export function isInt(value: string) {
  return parseInt(value) === Number(value);
}

export function isDecimal(value: string) {
  return parseFloat(value) === Number(value);
}

export function idToKey(id: string): string {
  return id.split("/")[1];
}

export function displayDecimal(value: any, format: Map<string, any> | undefined | null): string {
  if (value === FORMULA_ERROR_VALUE) {
    return t("field.error.value");
  }

  if (value === null || value === undefined || !isDecimal(value)) {
    return "";
  }

  if (format) {
    let numberFormat = format.get("numberFormat") || DEFAULT_NUMBER_FORMAT;
    const decimalPlaces = format.get("decimalPlaces") || DECIMAL_DEFAULT_DECIMAL_PLACES;

    if (numberFormat === "none") {
      return parseFloat(value).toFixed(decimalPlaces);
    } else {
      let options = {};
      if (decimalPlaces) {
        options = {
          minimumFractionDigits: decimalPlaces,
          maximumFractionDigits: decimalPlaces
        };
      }
      return value.toLocaleString(numberFormat, options);
    }
  } else {
    return parseFloat(value).toFixed(DECIMAL_DEFAULT_DECIMAL_PLACES);
  }
}

// A letter starts a word unless it follows a letter, a combining mark, a digit,
// an apostrophe or an underscore. Unlike \w and \b, this handles accented characters.
const WORD_START = /(^|[^\p{L}\p{M}\p{N}'’_])(\p{L})/gu;

export function displayText(value: any, format: Map<string, any> | undefined | null): string {
  if (!value || value.length === 0) {
    return "";
  }

  if (typeof value !== "string") {
    return String(value);
  }
  const textFormat = (format && format.get("textFormat")) ? format.get("textFormat") : DEFAULT_TEXT_FORMAT;
  switch (textFormat) {
    case "uppercase":
      return value.toUpperCase();
    case "lowercase":
      return value.toLowerCase();
    case "capitalize": {
      return value.toLowerCase().replace(WORD_START, (_, separator, letter) => separator + letter.toUpperCase());
    }
    default:
      return value;
  }
}

export function displayPercent(value: any, format: Map<string, any> | undefined | null): string {
  if (value === FORMULA_ERROR_VALUE) {
    return t("field.error.value");
  }

  if (value === null || value === undefined || !isDecimal(value)) {
    return "";
  }

  const decimalDisplay = displayDecimal(value, format);
  if (decimalDisplay !== "") {
    return decimalDisplay + " %";
  }
  return "";
}

export function displayCurrency(value: any, format: Map<string, any> | undefined | null): string {
  if (value === FORMULA_ERROR_VALUE) {
    return t("field.error.value");
  }

  if (value === null || value === undefined || !isDecimal(value)) {
    return "";
  }

  const decimalDisplay = displayDecimal(value, format);
  if (decimalDisplay !== "") {
    const currencySymbol = format?.get("symbol") || DEFAULT_CURRENCY_SYMBOL;
    const currencySymbolBefore = format?.get("before") || DEFAULT_CURRENCY_SYMBOL_BEFORE;

    return (currencySymbolBefore ? currencySymbol + " " + decimalDisplay : decimalDisplay + " " + currencySymbol);
  }
  return "";
}

export function displayNumber(value: any, format: Map<string, any> | undefined | null): string {
  if (value === FORMULA_ERROR_VALUE) {
    return t("field.error.value");
  }

  if (value === null || value === undefined || !isInt(value)) {
    return "";
  }

  if (!format) {
    return String(value);
  }

  let numberFormat = format.get("numberFormat") || DEFAULT_NUMBER_FORMAT;
  if (numberFormat === "none") {
    return String(value);
  } else {
    return value.toLocaleString(numberFormat);
  }
}

export function displayDate(value: any, dFormat: Map<string, any> | undefined | null, aggregation: AggregationType | undefined, t: TFunction): string {
  if (!value) {
    return "";
  }

  if (aggregation && (aggregation === AggregationType.DAY || aggregation === AggregationType.MONTH || aggregation === AggregationType.YEAR || aggregation === AggregationType.QUARTER || aggregation === AggregationType.WEEK)) {
    const period = Period.fromObject(value);
    if (!period) {
      return "";
    }
    const dateFormatKey = (dFormat && dFormat.get("periodFormat")) ? dFormat.get("periodFormat") : DEFAULT_PERIOD_FORMAT;
    const periodType = aggregation ? aggregationToPeriodType(aggregation) : period.type;
    const periodFormat = getPeriodFormats(periodType)?.get(dateFormatKey) || getPeriodFormats(periodType)?.get(DEFAULT_PERIOD_FORMAT);
    return period?.toString(t, periodFormat) || "";
  }

  const lang = useUserStore.getState().lang;
  const locales = useAppStore.getState().locales;
  let formattedDate: string = "";
  const dateFormatKey = (dFormat && dFormat.get("dateFormat")) ? dFormat.get("dateFormat") : DEFAULT_DATE_FORMAT;
  const dateFormat = DATE_FORMATS.get(dateFormatKey) || DATE_FORMATS.get(DEFAULT_DATE_FORMAT);
  if (isoDateRegex.test(value)) {
    const currentDate: Date = new UTCDate(value);
    formattedDate = format(currentDate, dateFormat!, { locale: locales.get(lang) });
  }
  return formattedDate;
}

export function periodRealType(periodType: PeriodType, aggregation: AggregationType | undefined): PeriodType {
  if (aggregation && aggregation !== AggregationType.NO_AGGR) {
    return aggregationToPeriodType(aggregation);
  }
  return periodType;
}

export function displayPeriod(value: any, periodType: PeriodType, dFormat: Map<string, any> | undefined | null, t: TFunction): string {
  if (!value) {
    return "";
  }

  const period = Period.fromObject(value);

  const periodFormats = (periodType !== null && periodType !== undefined) ? getPeriodFormats(periodType) : undefined;
  if (periodFormats) {
    const periodFormatKey = (dFormat && dFormat.get("periodFormat")) ? dFormat.get("periodFormat") : DEFAULT_PERIOD_FORMAT;
    const periodFormat = periodFormats.get(periodFormatKey) || periodFormats.get(DEFAULT_PERIOD_FORMAT);
    return period?.toString(t, periodFormat, periodType) || "";
  }
  if (periodType === PeriodTypeDay) {
    return period?.toString(t, DATE_FORMATS.get("NONE") || "yyyy-MM-dd", periodType) || "";
  }
  return period?.toString(t, DEFAULT_PERIOD_FORMAT, periodType) || "";
}

export function userToString(user: User): string {
  return user.username || user._key;
}

export function displayUser(value: any, defaultValue: string): string {
  if (!value) {
    return defaultValue;
  }

  const users = useAppStore.getState().usersByKeys;
  const user = users.get(value);
  if (user) {
    return userToString(user);
  }
  return defaultValue;
}

export function getUser(value: string): User | null {
  if (!value) {
    return null;
  }

  const users = useAppStore.getState().usersByKeys;
  const user = Array.from(users.values()).find(user => {
    if (user.username) {
      return user.username === value;
    }
    return user._key === value;
  });
  if (user) {
    return user;
  }
  return null;
}

export function posToString(position: CellPosition | null): string {
  if (!position) {
    return "null";
  }
  return "[" + position.rowId + "," + position.column + "]";
}

export function samePosition(pos1: CellPosition | null, pos2: CellPosition | null): boolean {
  if (!pos1 || !pos2) {
    return pos1 === pos2;
  }
  return pos1.rowId === pos2.rowId && pos1.column === pos2.column;
}

export function containsPosition(positions: CellPosition[] | null, position: CellPosition | null): boolean {
  if (!positions || !position) {
    return false;
  }
  return positions.some(p => samePosition(p, position));
}

export function rightPosition(position: CellPosition | null, colNumber: number): CellPosition | null {
  if (!position) {
    return null;
  }
  if (position.rowId === NEW_ROW_ID) {
    if (position.column < colNumber - 1) {
      return { rowId: NEW_ROW_ID, column: position.column + 1 };
    } else {
      return { rowId: NEW_ROW_ID, column: 0 };
    }
  }
  let currLine = position.rowId;
  let currCol = position.column;
  if (currCol < colNumber - 1) {
    let metaLines = useDataStore.getState().metaLines;
    let fieldColumns = useDataStore.getState().fieldColumns;
    let metaLine: MetaLine | null = metaLines.get(currLine) ?? null;
    if (metaLine === null) {
      return null;
    }
    if (metaLine.parentId === null) {
      return { rowId: currLine, column: currCol + 1 };
    }

    let parentMetaLine: MetaLine | null = metaLines.get(metaLine.parentId) ?? null;
    if (parentMetaLine === null) {
      return null;
    }
    // Case of an horizontal parent group
    if (parentMetaLine.line.isLeftGrouped === true) {
      let leftGroupPathKey = useDataStore.getState().fieldColumns[0].pathKey
      let destPathKey = fieldColumns[currCol + 1].pathKey
      if (leftGroupPathKey && (destPathKey === leftGroupPathKey || !isRootPath(leftGroupPathKey)) && destPathKey && extractFirstLinkPath(leftGroupPathKey) === extractFirstLinkPath(destPathKey)) {
        return { rowId: metaLine.parentId + ".0", column: currCol + 1 };
      } else {
        return { rowId: currLine, column: currCol + 1 };
      }
    } else {
      return { rowId: currLine, column: currCol + 1 };
    }
  } else {
    return position;
  }
}

export function nextPosition(position: CellPosition | null, colNumber: number): CellPosition | null {
  if (!position) {
    return null;
  }
  if (position.rowId === NEW_ROW_ID) {
    if (position.column < colNumber - 1) {
      return { rowId: NEW_ROW_ID, column: position.column + 1 };
    } else {
      return { rowId: NEW_ROW_ID, column: 0 };
    }
  }
  let currLine = position.rowId;
  let currCol = position.column;
  if (currCol < colNumber - 1) {
    return rightPosition(position, colNumber);
  } else {
    let metaLines = useDataStore.getState().metaLines;
    let metaLine: MetaLine | null = metaLines.get(currLine) ?? null;
    if (metaLine === null) {
      return position;
    }
    let fieldColumns = useDataStore.getState().fieldColumns
    let nextLineId = downMetaLineId(metaLine, fieldColumns[0].pathKey);
    if (nextLineId === null) {
      return position;
    }
    return { rowId: nextLineId, column: 0 };
  }
}

export function leftPosition(position: CellPosition | null, colNumber: number): CellPosition | null {
  if (!position) {
    return null;
  }
  if (position.rowId === NEW_ROW_ID) {
    if (position.column > 0) {
      return { rowId: NEW_ROW_ID, column: position.column - 1 };
    } else {
      return { rowId: NEW_ROW_ID, column: colNumber - 1 };
    }
  }
  let currLine = position.rowId;
  let currCol = position.column;
  if (currCol > 0) {
    let metaLines = useDataStore.getState().metaLines;
    let fieldColumns = useDataStore.getState().fieldColumns;
    let metaLine: MetaLine | null = metaLines.get(currLine) ?? null;
    if (metaLine === null) {
      return null;
    }
    if (metaLine.parentId === null) {
      return { rowId: currLine, column: currCol - 1 };
    }

    let parentMetaLine: MetaLine | null = metaLines.get(metaLine.parentId) ?? null;
    if (parentMetaLine === null) {
      return null;
    }
    // Case of an horizontal parent group
    if (parentMetaLine.line.isLeftGrouped === true) {
      let leftGroupPathKey = useDataStore.getState().fieldColumns[0].pathKey
      let destPathKey = fieldColumns[currCol - 1].pathKey
      if (leftGroupPathKey && (destPathKey === leftGroupPathKey || !isRootPath(leftGroupPathKey)) && destPathKey && extractFirstLinkPath(leftGroupPathKey) === extractFirstLinkPath(destPathKey)) {
        return { rowId: metaLine.parentId + ".0", column: currCol - 1 };
      } else {
        return { rowId: currLine, column: currCol - 1 };
      }
    } else {
      return { rowId: currLine, column: currCol - 1 };
    }
  } else {
    return position;
  }
}

export function prevPosition(position: CellPosition | null, colNumber: number): CellPosition | null {
  if (!position) {
    return null;
  }
  if (position.rowId === NEW_ROW_ID) {
    if (position.column > 0) {
      return { rowId: NEW_ROW_ID, column: position.column - 1 };
    } else {
      return { rowId: NEW_ROW_ID, column: colNumber - 1 };
    }
  }
  let currLine = position.rowId;
  let currCol = position.column;
  if (currCol > 0) {
    return leftPosition(position, colNumber);
  } else {
    let metaLines = useDataStore.getState().metaLines;
    let metaLine: MetaLine | null = metaLines.get(currLine) ?? null;
    if (metaLine === null) {
      return position;
    }
    let fieldColumns = useDataStore.getState().fieldColumns
    let prevLineId = upMetaLineId(metaLine, fieldColumns[fieldColumns.length - 1].pathKey);
    if (prevLineId === null) {
      return position;
    }
    return { rowId: prevLineId, column: colNumber - 1 };
  }
}

function downMetaLineId(metaLine: MetaLine, pathKey: string | null): string | null {
  let lines = useDataStore.getState().data;
  let metaLines = useDataStore.getState().metaLines;
  let groupNumber = useDataStore.getState().groupNumber;
  let lineHierarchy = metaLine.id.split(".");
  let depth = lineHierarchy.length;
  if (depth <= groupNumber) {
    return null;
  }
  let currIndex = Number(lineHierarchy[depth - 1]);

  // Case of no groups
  if (metaLine.parentId === null) {
    if (currIndex < metaLines.size - 1) {
      return String(currIndex + 1);
    }
    return null;

    // There is at least one group
  } else {

    let parentMetaLine: MetaLine | null = metaLines.get(metaLine.parentId) ?? null;
    if (parentMetaLine === null) {
      return null;
    }
    let simblingNbs = parentMetaLine.childrenIds.length;

    // Case of an horizontal parent group
    if (parentMetaLine.line.isLeftGrouped === true) {
      const parentIndex = Number(lineHierarchy[lineHierarchy.length - 2])
      const grandParentMetaLine: MetaLine | null = parentMetaLine.parentId ? metaLines.get(parentMetaLine.parentId) ?? null : null;
      const idPrefix = (grandParentMetaLine) ? grandParentMetaLine.id + "." : "";

      const parentSimblingsNumber = grandParentMetaLine ? grandParentMetaLine.childrenIds.length : lines.length

      let leftGroupPathKey = useDataStore.getState().fieldColumns[0].pathKey
      if (leftGroupPathKey && (pathKey === leftGroupPathKey || !isRootPath(leftGroupPathKey)) && pathKey && extractFirstLinkPath(leftGroupPathKey) === extractFirstLinkPath(pathKey)) {
        const lastIndex = parentSimblingsNumber - 1;
        if (parentIndex < lastIndex) {
          return idPrefix + String(parentIndex + 1) + ".0"
        } else {
          return null;
        }
      } else {
        if (currIndex < simblingNbs - 1) {
          return parentMetaLine.id + "." + (currIndex + 1);
        } else if (parentIndex < parentSimblingsNumber - 1) {
          return idPrefix + String(parentIndex + 1) + ".0"
        } else {
          return null
        }
      }
    }
    // Case of a vertical parent group
    else {
      if (currIndex < simblingNbs - 1) {
        return parentMetaLine.id + "." + (currIndex + 1);
      } else {
        return null
      }
    }
  }
}

export function downPosition(position: CellPosition | null): CellPosition | null {
  if (position === null) {
    return null;
  }
  if (position.rowId === NEW_ROW_ID) {
    return null;
  }
  let metaLines = useDataStore.getState().metaLines;
  let fieldColumns = useDataStore.getState().fieldColumns;
  let metaLine: MetaLine | null = metaLines.get(position.rowId) ?? null;
  if (metaLine === null) {
    return null;
  }
  let fieldColumn = fieldColumns[position.column]
  let downId = downMetaLineId(metaLine, fieldColumn.pathKey);
  if (downId === null) {
    return position;
  }
  let currCol = position.column;
  return { rowId: downId, column: currCol };
}


function upMetaLineId(metaLine: MetaLine, pathKey: string | null): string | null {
  let metaLines = useDataStore.getState().metaLines;
  let groupNumber = useDataStore.getState().groupNumber;
  let lineHierarchy = metaLine.id.split(".");
  let depth = lineHierarchy.length;
  if (depth <= groupNumber) {
    return null;
  }
  let currIndex = Number(lineHierarchy[depth - 1]);

  // Case of no groups
  if (metaLine.parentId === null) {
    if (currIndex > 0) {
      return String(currIndex - 1);
    }
    return null;

    // There is at least one group
  } else {

    let parentMetaLine: MetaLine | null = metaLines.get(metaLine.parentId) ?? null;
    if (parentMetaLine === null) {
      return null;
    }

    // Case of an horizontal parent group
    if (parentMetaLine.line.isLeftGrouped === true) {
      const parentIndex = Number(lineHierarchy[lineHierarchy.length - 2])
      const grandParentMetaLine: MetaLine | null = parentMetaLine.parentId ? metaLines.get(parentMetaLine.parentId) ?? null : null;
      const idPrefix = (grandParentMetaLine) ? grandParentMetaLine.id + "." : "";

      let leftGroupPathKey = useDataStore.getState().fieldColumns[0].pathKey
      if (leftGroupPathKey && (pathKey === leftGroupPathKey || !isRootPath(leftGroupPathKey)) && pathKey && extractFirstLinkPath(leftGroupPathKey) === extractFirstLinkPath(pathKey)) {
        if (parentIndex > 0) {
          return idPrefix + String(parentIndex - 1) + ".0"
        } else {
          return null;
        }
      } else {
        if (currIndex > 0) {
          return parentMetaLine.id + "." + (currIndex - 1);
        } else if (parentIndex > 0) {
          const previousParentLine: MetaLine | null = metaLines.get(idPrefix + String(parentIndex - 1)) ?? null;
          if (previousParentLine) {
            return idPrefix + String(parentIndex - 1) + "." + String(previousParentLine.childrenIds.length - 1)
          } else {
            return idPrefix + String(parentIndex - 1) + ".0"
          }
        } else {
          return null
        }
      }
    }
    // Case of a vertical parent group
    else {
      if (currIndex > 0) {
        return parentMetaLine.id + "." + (currIndex - 1);
      } else {
        return null
      }
    }
  }
}

export function upPosition(position: CellPosition | null): CellPosition | null {
  if (position === null) {
    return null;
  }
  if (position.rowId === NEW_ROW_ID) {
    return null;
  }
  let metaLines = useDataStore.getState().metaLines;
  let metaLine: MetaLine | null = metaLines.get(position.rowId) ?? null;
  if (metaLine === null) {
    return null;
  }
  const fieldColumns = useDataStore.getState().fieldColumns
  let fieldColumn = fieldColumns[position.column]
  let upId = upMetaLineId(metaLine, fieldColumn.pathKey);
  if (upId === null) {
    return position;
  }
  let currCol = position.column;
  return { rowId: upId, column: currCol };
}

// export function nextLinePosition(position: CellPosition | null, lineNumber: number): CellPosition | null {
//   if (!position) {
//     return null;
//   }
//   let currLine = position.line;
//   let currCol = position.column;
//   if (currLine < lineNumber - 1) {
//     return { line: currLine + 1, column: currCol };
//   } else {
//     return { line: 0, column: currCol };
//   }
// }

// export function prevLinePosition(position: CellPosition | null, lineNumber: number): CellPosition | null {
//   if (!position) {
//     return null;
//   }
//   let currLine = position.line;
//   let currCol = position.column;
//   if (currLine > 0) {
//     return { line: currLine - 1, column: currCol };
//   } else {
//     return { line: lineNumber - 1, column: currCol };
//   }
// }

export function itemToString(item: Item, field: Field, aggregation: AggregationType | undefined, format: Map<string, any> | undefined | null, t: TFunction, defaultValue: string, fieldByKeys: Map<string, Field> | null): string {
  if ((!item || !item.key?.recordKey) && !item?.value) {
    return defaultValue;
  }
  return valueToString(item.value, field, aggregation, format, t, defaultValue, fieldByKeys);
}

export function valueToString(value: any, field: Field, aggregation: AggregationType | undefined, format: Map<string, any> | undefined | null, t: TFunction, defaultValue: string, fieldByKeys: Map<string, Field> | null): string {
  if (!field) {
    return defaultValue;
  }

  if (isFormulaError(value, field, fieldByKeys)) {
    return t("field.error.value");
  }

  if (value === null || value === undefined) {
    const outputType = fieldToOutputType(field, aggregation, fieldByKeys);
    if (outputType === FieldType.Boolean) {
      return t("data.type.boolean.false");
    }
    return defaultValue;
  }

  if (value && value.value) {
    return valueToString(value.value, field, aggregation, format, t, defaultValue, fieldByKeys);
  }


  if (isCountAggregation(aggregation)) {
    return displayNumber(value, DEFAULT_NUMBER_FORMAT);
  }

  if (Array.isArray(value)) {
    // Map each value to string representation
    return value.map((item: any) => valueToString(item, field, aggregation, format, t, defaultValue, fieldByKeys)).join(", ");
  }

  switch (field.type) {
    case FieldType.Text:
      return displayText(value, format);
    case FieldType.TextArea:
      return displayText(value, format);
    case FieldType.Url:
      return displayText(value, format);
    case FieldType.Number:
      return displayNumber(value, format);
    case FieldType.Decimal:
      return displayDecimal(value, format);
    case FieldType.Percent:
      return displayPercent(value, format);
    case FieldType.Date:
      return displayDate(value, format, aggregation, t);
    case FieldType.Period:
      const periodType = field.typeItems?.get(TYPE_ITEMS_PERIOD_TYPE);
      if (!periodType) {
        return "";
      }
      const realPeriodType = periodRealType(periodType, aggregation);
      return displayPeriod(value, realPeriodType, format, t);
    case FieldType.Devise:
      return displayCurrency(value, format);
    case FieldType.Choice:
      if (typeof value === "string") {
        return value;
      } else {
        return (value as string[])[0];
      }
    case FieldType.MultiChoice:
      if (typeof value === "string") {
        return value;
      } else {
        return (value as string[]).join(",");
      }
    case FieldType.User:
      return displayUser(value, defaultValue);
    case FieldType.Boolean:
      return value ? t("data.type.boolean.true") : t("data.type.boolean.false");
    case FieldType.Link:
      const destFieldKey = field.typeItems?.get(TYPE_ITEMS_FIELD_KEY);
      if (destFieldKey) {
        const destField = fieldByKeys?.get(destFieldKey);
        if (destField) {
          return valueToString(value, destField, aggregation, format, t, defaultValue, fieldByKeys);
        }
      }
      // if (isArray(value)) {
      //   return value.map((item: Item) => valueToString(item.value, item.key?.field, aggregation, t, "")).join(", ");
      // }
      // Should never happen
      return JSON.stringify(value);
    default:
      // Should never happen
      return JSON.stringify(value);
  }
}

export function copyLines(lines: Line[]): Line[] {
  return lines.map((line) => {
    return { items: line.items, children: line.children ? copyLines(line.children) : null, isLeftGrouped: line.isLeftGrouped }
  })
}

function getLineFromIndexes(lines: Line[], lineHierarchy: number[]): Line | null {

  if (lineHierarchy.length === 0) {
    return null;
  }

  const lineIndex = lineHierarchy[0];
  let line: Line | null = null

  if (lineIndex < lines.length) {
    line = lines[lineIndex];
  } else {
    return null;
  }

  if (lineHierarchy.length === 1) {
    return line;
  } else {
    if (!line.children) {
      return null;
    }
    return getLineFromIndexes(line.children, lineHierarchy.slice(1));
  }
}

export function getLine(lines: Line[], rowId: string): Line | null {
  const lineHierarchy = rowId.split(".").map((str) => parseInt(str));
  return getLineFromIndexes(lines, lineHierarchy);
}

export function toMetaLine(line: Line, level: number, parentId: string | null, index: number): MetaLine {
  const childrenNb = line.children ? line.children.length : 0;
  const childrenIds = [];
  for (let i = 0; i < childrenNb; i++) {
    childrenIds.push(parentId ? parentId + "." + index + "." + i : index + "." + i);
  }
  return {
    id: parentId ? parentId + "." + index : index.toString(),
    line: line,
    parentId: parentId,
    childrenIds: childrenIds,
    level: level,
  };
}

export function toMetaLineMap(lines: Line[], level: number = 0, parentId: string | null = null): Map<string, MetaLine> {

  const metaLines = new Map<string, MetaLine>();
  if (!lines) {
    return metaLines;
  }

  lines.forEach((line, index) => {
    const metaLine = toMetaLine(line, level, parentId, index);
    // Assign stable rowId to line (same as metaLine.id)
    // This ID won't change with frontend filtering
    line._rowId = metaLine.id;
    metaLines.set(metaLine.id, metaLine);

    if (line.children) {
      const childMetaLineMap = toMetaLineMap(line.children, level + 1, metaLine.id);
      childMetaLineMap.forEach((childMetaLine) => {
        metaLines.set(childMetaLine.id, childMetaLine);
      });
    }
  });

  return metaLines;
}

export function isArray(value: any): boolean {
  return Array.isArray(value) || (typeof value === "object" && value?.constructor === Array);
}

export function isCompatible(value: string, field: Field, format: Map<string, any> | undefined | null): boolean {
  switch (field.type) {
    case FieldType.Text:
      return true;
    case FieldType.TextArea:
      return true;
    case FieldType.Url:
      return true;
    case FieldType.User:
      return true;
    case FieldType.Number:
      return isInt(value);
    case FieldType.Decimal:
      return isDecimal(value);
    case FieldType.Date:
      return Period.isCompatibleToDateLayout(value, DEFAULT_DATE_FORMAT);
    case FieldType.Devise:
      const currencySymbol = format?.get("symbol") || DEFAULT_CURRENCY_SYMBOL;
      value = value.replace(currencySymbol, "").trim();
      return isDecimal(value);
    case FieldType.Percent:
      const percentSymbol = "%";
      value = value.replace(percentSymbol, "").trim();
      return isDecimal(value);
    case FieldType.Period:
      const periodType = field.typeItems?.get(TYPE_ITEMS_PERIOD_TYPE);
      if (!periodType) {
        return false;
      }
      if (periodType === PeriodTypeDay) {
        return isoDateRegex.test(value);
      }
      if (periodType === PeriodTypeYear) {
        return isInt(value) && value.length <= 4;
      }
      return false;
    case FieldType.Choice:
      return false;
    case FieldType.MultiChoice:
      return false;
    case FieldType.Boolean:
      return false;
    default:
      return true;
  }
}

export function isValidatable(value: string, field: Field, format: Map<string, any> | undefined | null): boolean {
  switch (field.type) {
    case FieldType.Text:
      return true;
    case FieldType.TextArea:
      return true;
    case FieldType.Url:
      return true;
    case FieldType.Number:
      return isInt(value);
    case FieldType.Decimal:
      return isDecimal(value);
    case FieldType.Date:
      return Period.isCompatibleToDateLayout(value, DEFAULT_DATE_FORMAT);
    case FieldType.Devise:
      const currencySymbol = format?.get("symbol") || DEFAULT_CURRENCY_SYMBOL;
      value = value.replace(currencySymbol, "").trim();
      return isDecimal(value);
    case FieldType.Percent:
      const percentSymbol = "%";
      value = value.replace(percentSymbol, "").trim();
      return isDecimal(value);
    case FieldType.Period:
      const periodType = field.typeItems?.get(TYPE_ITEMS_PERIOD_TYPE);
      if (!periodType) {
        return false;
      }
      if (periodType === PeriodTypeDay) {
        return isoDateRegex.test(value);
      }
      if (periodType === PeriodTypeYear) {
        return isInt(value) && value.length === 4;
      }
      return false;
    case FieldType.Choice:
      return false;
    case FieldType.MultiChoice:
      return false;
    case FieldType.User:
      return false;
    case FieldType.Boolean:
      return false;
    default:
      return true;
  }
}

export function removeFormat(value: any, field: Field, format: Map<string, any> | undefined | null): string | null {
  const result = removeNumberFormat(value, format?.get("numberFormat"))
  return result;
}

export function guessNumberFormat(value: string): string {

  if (value.match(/[0-9]+,[0-9,]+\.[0-9]+/) !== null || value.match(/[0-9]{1,3},[0-9]{3},[0-9,]+(\.[0-9]+)?/) !== null) {
    return "en-US"
  }
  else if (value.match(/[0-9]+\.[0-9.]+,[0-9]+/) !== null || value.match(/[0-9]{1,3}\.[0-9]{3}\.[0-9.]+(,[0-9]+)?/) !== null) {
    return "de-DE"
  }
  else if (value.match(/[0-9,]+,[0-9]{1,2}$/) !== null) {
    return "fr-FR"
  }
  else {
    return "fr-FR"
  }
}

export function removeNumberFormat(value: any, numberFormat: string): string | null {

  if (!value) {
    return null;
  }

  let result = removeSymbols(value);
  switch (numberFormat) {
    case 'fr-FR':
      result = removeSpaces(result);
      result = result.replace(',', '.').trim();
      break;
    case 'en-US':
      result = result.replaceAll(",", "").trim();
      break;
    case 'de-DE':
      result = result.replaceAll(".", "").trim();
      result = result.replace(',', '.').trim();
      break;
    default:
      break;
  }
  return result;
}

export function removeSymbols(value: any): string {
  // Need to stringify the value because not all values are string (e.g non-formatted integers) 
  let result = value.toString();

  const percentSymbol = "%";
  result = result.replace(percentSymbol, "").trim();

  for (const currencySymbol of CURRENCY_SYMBOLS) {
    result = result.replace(currencySymbol, "").trim();
  }
  return result;
}

export function removeSpaces(value: any): string {
  let result = value.toString();
  result = result.replaceAll(' ', '').trim(); // Non-breaking space
  result = result.replaceAll(" ", "").trim();
  return result;
}

export function textToValue(text: string, field: Field, t: TFunction, aggregation: AggregationType | undefined = undefined, format: Map<string, any> | undefined | null = undefined, fieldByKeys: Map<string, Field> | null): any {
  const realFieldType = fieldToOutputType(field, aggregation, fieldByKeys);
  return textToValueByType(text, realFieldType, t, aggregation, format, field.typeItems);
}

export function textToValueByType(text: string, fieldType: FieldType, t: TFunction, aggregation: AggregationType | undefined = undefined, format: Map<string, any> | undefined | null = undefined, typeItems: Map<string, any> | undefined | null = undefined): any {
  let formattedText: string | null = null;
  switch (fieldType) {
    case FieldType.Text:
      return text;
    case FieldType.TextArea:
      return text.replace(/^"+/, "").replace(/"+$/, "");
    case FieldType.Url:
      return text;
    case FieldType.Number:
      if (!text || text.length === 0) {
        return null;
      }
      if (text === "-") {
        return 0;
      }
      formattedText = removeNumberFormat(text, format?.get("numberFormat") || DEFAULT_NUMBER_FORMAT);
      if (!formattedText) {
        return null;
      }
      return parseInt(formattedText);
    case FieldType.Decimal:
      if (!text || text.length === 0) {
        return null;
      }
      if (text === "-") {
        return 0;
      }
      formattedText = removeNumberFormat(text, format?.get("numberFormat") || DEFAULT_NUMBER_FORMAT);
      if (!formattedText) {
        return null;
      }
      return parseFloat(formattedText);
    case FieldType.Devise:
      if (!text || text.length === 0) {
        return null;
      }
      if (text === "-") {
        return 0;
      }
      formattedText = removeNumberFormat(text, format?.get("numberFormat") || DEFAULT_NUMBER_FORMAT);
      if (!formattedText) {
        return null;
      }
      return parseFloat(text);
    case FieldType.Percent:
      if (!text || text.length === 0) {
        return null;
      }
      if (text === "-") {
        return 0;
      }
      formattedText = removeNumberFormat(text, format?.get("numberFormat") || DEFAULT_NUMBER_FORMAT);
      if (!formattedText) {
        return null;
      }
      return parseFloat(formattedText);
    case FieldType.Date:
      if (!text || text.length === 0) {
        return null;
      }
      const dateFormatName = format?.get("dateFormat") || DEFAULT_DATE_FORMAT;
      const dateFormat = DATE_FORMATS.get(dateFormatName) || DATE_FORMATS.get(DEFAULT_DATE_FORMAT) || "yyyy-MM-dd";
      const lang = useUserStore.getState().lang;
      const locales = useAppStore.getState().locales;
      const date = parse(text, dateFormat, new UTCDate(), { locale: locales.get(lang) });
      if (date && !isNaN(date.getTime())) {
        return date.toISOString().substring(0, 10);
      }
      return null;
    case FieldType.Period:

      const periodType = (aggregation && aggregation !== AggregationType.NO_AGGR) ? aggregationToPeriodType(aggregation) : typeItems?.get(TYPE_ITEMS_PERIOD_TYPE);
      if (periodType === null || periodType === undefined) {
        return null;
      }
      switch (periodType) {
        case PeriodTypeDay: {
          if (!text || text.length === 0) {
            return null;
          }
          const dateFormatName = typeItems?.get(TYPE_ITEMS_DATE_FORMAT) || DEFAULT_DATE_FORMAT;
          const dateFormat = DATE_FORMATS.get(dateFormatName) || DATE_FORMATS.get(DEFAULT_DATE_FORMAT) || "yyyy-MM-dd";
          const lang = useUserStore.getState().lang;
          const locales = useAppStore.getState().locales;
          const date = parse(text, dateFormat, new UTCDate(), { locale: locales.get(lang) });
          if (date && !isNaN(date.getTime())) {
            return Period.fromDate(date, periodType);
          }
          return null;
        }
        case PeriodTypeYear: {
          if (!text || text.length === 0) {
            return null;
          }
          const year = parseInt(text);
          return Period.newPeriod(year, PeriodMonthJanuary, 1, PeriodTypeYear);
        }
        case PeriodTypeWeek: {
          if (!text || text.length === 0) {
            return null;
          }
          const weekParts = text.split(" ");
          if (weekParts.length !== 2) {
            return null;
          }
          const year = parseInt(weekParts[1]);
          const week = Period.weekValue(weekParts[0], t);
          return getWeekPeriod(week, year);
        }
        case PeriodTypeMonth: {
          if (!text || text.length === 0) {
            return null;
          }
          const monthParts = text.split(" ");
          if (monthParts.length !== 2) {
            return null;
          }
          const year = parseInt(monthParts[1]);
          const month = Period.monthValue(monthParts[0], t);
          return getMonthPeriod(month, year);
        }
        case PeriodTypeQuarter: {
          if (!text || text.length === 0) {
            return null;
          }
          const quarterParts = text.split(" ");
          if (quarterParts.length !== 2) {
            return null;
          }
          const year = parseInt(quarterParts[1]);
          const quarter = Period.quarterValue(quarterParts[0], t);
          return getQuarterPeriod(quarter, year);
        }
      }
      return Period.create(text, periodType);
    case FieldType.Choice:
      return [text];
    case FieldType.MultiChoice:
      return text.split(",").map(item => item.trim());
    case FieldType.User:
      if (!text) {
        return null;
      }
      const user = getUser(text);
      if (user) {
        return user._key;
      }
      return null;
    case FieldType.Boolean:
      if (!text) {
        return false;
      }
      if (text === t("data.type.boolean.true")) {
        return true;
      }
      return false;
    case FieldType.Link:
      return null;
    default:
      return text;
  }
}

export function compareValues(value1: any, value2: any, field: Field, aggregation: AggregationType | undefined, sortType: SortType, t: TFunction, fieldByKeys: Map<string, Field> | null): number {
  if (sortType === DESC) {
    return -compareValues(value1, value2, field, aggregation, ASC, t, fieldByKeys);
  }

  const v1 = textToValue(value1, field, t, aggregation, null, fieldByKeys);
  const v2 = textToValue(value2, field, t, aggregation, null, fieldByKeys);
  if (v1 === null && v2 === null) {
    return 0;
  }
  if (v1 === null) {
    return -1;
  }
  if (v2 === null) {
    return 1;
  }
  if (v1 === v2) {
    return 0;
  }
  if (field.type === FieldType.Choice || field.type === FieldType.MultiChoice) {
    if (isArray(v1) && isArray(v2) && v1.length > 0 && v2.length > 0) {
      return v1[0].localeCompare(v2[0]);
    }
    if (isArray(v1) && v1.length > 0) {
      return -1;
    }
    if (isArray(v2) && v2.length > 0) {
      return 1;
    }
    return 0;
  }
  switch (typeof v1) {
    case "string":
      return v1.localeCompare(v2);
    case "number":
      return v1 - v2;
    case "boolean":
      return v1 ? 1 : -1;
    case "object":
      // Case of arrays: should not happen but just in case
      if (isArray(v1) || isArray(v2)) {
        return 0
      }
      const period1 = Period.fromObject(v1);
      const period2 = Period.fromObject(v2);
      if (period1 && period2) {
        return period1.date.localeCompare(period2.date);
      }
      return 0;
    default:
      return 0;
  }
}

export function getDefaultAggregation(field: Field | undefined): AggregationType | undefined {
  if (!field) {
    return undefined;
  }
  switch (field.type) {
    case FieldType.Number:
      return AggregationType.SUM;
    case FieldType.Decimal:
      return AggregationType.SUM;
    case FieldType.Devise:
      return AggregationType.SUM;
    case FieldType.Percent:
      return AggregationType.SUM;
    case FieldType.Date:
      return AggregationType.NO_AGGR;
    case FieldType.Period:
      return AggregationType.MONTH;
    default:
      return AggregationType.VALUE;
  }
}

export function getDefaultNumericAggregation(field: Field | undefined): AggregationType | undefined {
  if (!field) {
    return undefined;
  }
  switch (field.type) {
    case FieldType.Number:
      return AggregationType.SUM;
    case FieldType.Decimal:
      return AggregationType.SUM;
    case FieldType.Devise:
      return AggregationType.SUM;
    case FieldType.Percent:
      return AggregationType.SUM;
    default:
      return AggregationType.COUNT;
  }
}

export function valueStringOrDefault(value: any, defaultValue: any): any {
  if (value === FORMULA_ERROR_VALUE) {
    return value;
  }
  if (value === null || value === undefined || isNaN(value)) {
    return defaultValue;
  }
  return value.toString();
}

// Escape special regex characters
export function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// From input value, convert to the corresponding value type
export function inputRefToValue(inputValue: any, field: Field): any {
  const fieldType = field.type;
  switch (fieldType) {
    case FieldType.Number:
      return parseInt(inputValue);
    case FieldType.Decimal:
      return parseFloat(inputValue);
    case FieldType.Devise:
      return parseFloat(inputValue);
    case FieldType.Percent:
      return parseFloat(inputValue);
    case FieldType.Period:
      const periodType = field.typeItems?.get(TYPE_ITEMS_PERIOD_TYPE);
      if (!periodType) {
        return inputValue;
      }
      if (periodType === PeriodTypeYear) {
        return Period.newPeriod(parseInt(inputValue, 10), PeriodMonthJanuary, 1, PeriodTypeYear);
      }
      return Period.create(inputValue, periodType);
  }
  return inputValue;
}